import { APRContent } from 'components/modules/IslandList/IslandListItem/APRColumn'
import { ManageButtonFlex } from 'components/PositionListItem/styled'
import { UnderlyingTokenAmounts, useCompactPositionRowModel } from 'hooks/portfolio/useCompactPositionRowModel'
import { useCallback, useState } from 'react'
import { StyledInternalLink } from 'theme'
import { StakingTokenType } from 'types/farms'
import { ApiVaultData, BaultPosition, FarmPosition, IslandPosition } from 'types/portfolio'
import { formatDollarAmount } from 'utils/portfolio/formatDollar'
import { formatPoolFeeTierPercent, getVaultProviderLabel, getVaultProviderPoolType } from 'utils/portfolio/poolDisplay'

import { FarmClaimButton } from './FarmClaimButton'
import { PortfolioConnectorLines } from './PortfolioConnectorLines'
import { PortfolioMobileActionsFooter } from './PortfolioMobileActionsFooter'
import { PortfolioRangeDisplay } from './PortfolioRangeDisplay'
import { PortfolioManageButton } from './PortfolioRowPrimitives'
import { PortfolioSchemaRow } from './PortfolioSchemaRow'
import { PositionSubRow } from './PositionSubRow'
import { TokenPairLogo } from './TokenPairLogo'

interface CompactPositionRowProps {
  vault: ApiVaultData
  baultPositions?: BaultPosition[]
  islandPosition?: IslandPosition
  farmPositions?: FarmPosition[]
  baultUnderlyingBalances?: UnderlyingTokenAmounts
  islandUnderlyingBalances?: UnderlyingTokenAmounts
  v2UnderlyingBalances?: UnderlyingTokenAmounts
  charmUnderlyingBalances?: UnderlyingTokenAmounts
  farmUnderlyingBalances?: Record<string, UnderlyingTokenAmounts>
  onClaimSuccess?: (providerKey: 'kodiak' | 'bgt') => void
}

export function CompactPositionRow({
  vault,
  baultPositions,
  islandPosition,
  baultUnderlyingBalances,
  islandUnderlyingBalances,
  v2UnderlyingBalances,
  charmUnderlyingBalances,
  farmPositions,
  farmUnderlyingBalances,
  onClaimSuccess,
}: CompactPositionRowProps) {
  const [providerEarningsState, setProviderEarningsState] = useState<Partial<Record<'kodiak' | 'bgt', boolean>>>({})
  const poolTypeLabel = getVaultProviderLabel(vault.provider)
  const feeTierLabel = formatPoolFeeTierPercent(vault.pool?.fee)
  const subtitle = feeTierLabel ? `${feeTierLabel} • ${poolTypeLabel}` : poolTypeLabel

  const handleProviderEarningsStateChange = useCallback(
    (providerKey: 'kodiak' | 'bgt', hasCurrentEarnings: boolean) => {
      setProviderEarningsState((prev) => {
        if (prev[providerKey] === hasCurrentEarnings) return prev
        return { ...prev, [providerKey]: hasCurrentEarnings }
      })
    },
    []
  )

  const { totalValue, aggregatedData, allPositions, hasExpandableContent, manageRoute, claimModalType } =
    useCompactPositionRowModel({
      vault,
      baultPositions,
      islandPosition,
      farmPositions,
      baultUnderlyingBalances,
      islandUnderlyingBalances,
      v2UnderlyingBalances,
      charmUnderlyingBalances,
      farmUnderlyingBalances,
    })

  if (!vault.token0 || !vault.token1) return null

  const token0 = vault.token0
  const token1 = vault.token1
  const portfolioPoolType = getVaultProviderPoolType(vault.provider)
  const mobileRangeContent = (
    <div className="flex items-start justify-between gap-3">
      <div className="text-kodiak-text-secondary text-xs">Range</div>
      <div className="min-w-0">
        <PortfolioRangeDisplay
          token0Address={token0.id || token0.address}
          token1Address={token1.id || token1.address}
          lowerTick={vault.lowerTick}
          upperTick={vault.upperTick}
          currentTick={vault.currentTick}
          feeTier={vault.feeTier ?? vault.pool?.fee}
          provider={vault.provider}
          poolType={portfolioPoolType}
          showLabelsOnMobile
        />
      </div>
    </div>
  )

  const resolveUnderlyingBalances = (entry: (typeof allPositions)[number]) => {
    if (entry.type === 'Island') {
      if (vault.provider === StakingTokenType.V2) return v2UnderlyingBalances
      if (vault.provider === StakingTokenType.CHARM) return charmUnderlyingBalances
      return islandUnderlyingBalances
    }

    if (entry.type === 'Bault') {
      return baultUnderlyingBalances
    }

    if (vault.provider === StakingTokenType.V2) {
      return v2UnderlyingBalances
    }

    return farmUnderlyingBalances?.[entry.position.farm.id]
  }

  const resolveFarmClaimAction = (entry: (typeof allPositions)[number]) => {
    if (entry.type !== 'Farm' || !claimModalType) return undefined

    const farmPosition = entry.position as FarmPosition
    const hasFarmRewards = (farmPosition.claimableRewards || []).some((reward) => BigInt(reward.amount || '0') > 0n)
    if (!hasFarmRewards) return undefined

    const providerKey: 'kodiak' | 'bgt' = farmPosition.provider === 'bgt' ? 'bgt' : 'kodiak'
    const matchesClaimType =
      (farmPosition.provider === 'bgt' && claimModalType === 'vaultClaim') ||
      (farmPosition.provider === 'kodiak' && claimModalType === 'farmClaim')

    if (!matchesClaimType) return undefined

    return (
      <FarmClaimButton
        modalType={claimModalType}
        providerKey={providerKey}
        onEarningsStateChange={handleProviderEarningsStateChange}
        onClaimSuccess={onClaimSuccess}
      />
    )
  }

  const shouldHideFarmRewards = (entry: (typeof allPositions)[number]) => {
    if (entry.type !== 'Farm') return false
    const farmPosition = entry.position as FarmPosition
    const providerKey: 'kodiak' | 'bgt' = farmPosition.provider === 'bgt' ? 'bgt' : 'kodiak'
    return providerEarningsState[providerKey] === false
  }

  return (
    <PortfolioSchemaRow
      hasExpandableContent={hasExpandableContent}
      desktopClassName="items-center py-3 px-6 gap-4"
      desktopStyle={{
        gridTemplateColumns:
          'minmax(0, 1.45fr) minmax(0, 150px) minmax(0, 1fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 0.8fr)',
      }}
      identity={{
        leading: <TokenPairLogo token0={token0} token1={token1} size="md" />,
        title: `${token0.symbol}-${token1.symbol}`,
        subtitle,
      }}
      mobile={{
        value: (
          <div className="text-kodiak-text-primary font-semibold text-sm sm:text-base">
            {formatDollarAmount(totalValue)}
          </div>
        ),
        rootClassName: 'flex items-center justify-between',
        leftClassName: 'flex items-center space-x-3 min-w-0',
        valueRowClassName: 'flex items-center space-x-2',
      }}
      desktop={{
        showExpandIcon: true,
        columns: (
          <>
            <div>
              <PortfolioRangeDisplay
                token0Address={token0.id || token0.address}
                token1Address={token1.id || token1.address}
                lowerTick={vault.lowerTick}
                upperTick={vault.upperTick}
                currentTick={vault.currentTick}
                feeTier={vault.feeTier ?? vault.pool?.fee}
                provider={vault.provider}
                poolType={portfolioPoolType}
              />
            </div>

            <div className="text-right">
              {aggregatedData ? (
                <div className="space-y-0.5">
                  <div className="text-xs text-kodiak-text-primary flex items-center justify-end gap-1">
                    <span>
                      {aggregatedData.totalToken0Display} {token0.symbol}
                    </span>
                    <span className="text-[#7B97B7]">
                      ({formatDollarAmount(aggregatedData.totalToken0 * (token0.price || 0))})
                    </span>
                  </div>
                  <div className="text-xs text-kodiak-text-primary flex items-center justify-end gap-1">
                    <span>
                      {aggregatedData.totalToken1Display} {token1.symbol}
                    </span>
                    <span className="text-[#7B97B7]">
                      ({formatDollarAmount(aggregatedData.totalToken1 * (token1.price || 0))})
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-kodiak-text-secondary text-xs">—</div>
              )}
            </div>

            <div className="text-right">
              {aggregatedData?.aprData ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <APRContent aprData={aggregatedData.aprData} variant="portfolio" />
                </div>
              ) : (
                <div className="text-kodiak-text-secondary text-xs">—</div>
              )}
            </div>

            <div className="text-kodiak-text-primary font-semibold text-right text-base">
              {formatDollarAmount(totalValue)}
            </div>

            <div className="text-right">
              {aggregatedData && aggregatedData.unclaimedRewardsDisplay.length > 0 ? (
                <div className="text-sm text-[#6EE7B7] space-y-1">
                  {aggregatedData.unclaimedRewardsDisplay.map((reward) => (
                    <div key={reward.key}>
                      <span>
                        {reward.amountFormatted} {reward.symbol}
                      </span>{' '}
                      <span className="text-[#6EE7B7]">({reward.usdValue})</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-kodiak-text-secondary text-xs">—</div>
              )}
            </div>

            <ManageButtonFlex style={{ justifyContent: 'flex-end' }}>
              <PortfolioManageButton
                as={StyledInternalLink}
                to={manageRoute}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                Manage
              </PortfolioManageButton>
            </ManageButtonFlex>
          </>
        ),
      }}
      renderExpanded={() => (
        <div className="bg-kodiak-bg-module relative">
          {allPositions.map((entry, index) => {
            const isFirstItem = index === 0
            const isLastItem = index === allPositions.length - 1

            return (
              <div key={entry.id} className="relative">
                <PortfolioConnectorLines
                  isFirst={isFirstItem}
                  isLast={isLastItem}
                  mobileVerticalTop={isFirstItem ? '0' : undefined}
                  mobileTop={isFirstItem ? '66px' : '20px'}
                />
                <PositionSubRow
                  type={entry.type}
                  value={entry.value}
                  vault={vault}
                  position={entry.position}
                  mobileTopContent={isFirstItem ? mobileRangeContent : undefined}
                  underlyingBalances={resolveUnderlyingBalances(entry)}
                  isLast={isLastItem}
                  hideFarmRewards={shouldHideFarmRewards(entry)}
                  actions={resolveFarmClaimAction(entry)}
                />
              </div>
            )
          })}

          <PortfolioMobileActionsFooter>
            <PortfolioManageButton
              as={StyledInternalLink}
              to={manageRoute}
              style={{ width: '100%' }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              Manage
            </PortfolioManageButton>
          </PortfolioMobileActionsFooter>
        </div>
      )}
    />
  )
}
